import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { getJobList } from "../../utils/Api";

interface DocOption {
  id: string;
  name: string;
}

interface DocumentSelectProps {
  value: DocOption | null;
  onChange: (doc: DocOption | null) => void;
}

const DocumentSelect: React.FC<DocumentSelectProps> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [docs, setDocs] = useState<DocOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce query
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(query), 250);
  }, [query]);

  // Fetch docs when dropdown opens or query changes
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    getJobList({
      ...(debouncedQuery ? { search: debouncedQuery } : {}),
      limit: 50,
    })
      .then(({ data }) => {
        if (cancelled) return;
        const entries: DocOption[] = Object.entries(
          (data.documents as Record<string, { name: string }>) ?? {},
        ).map(([id, doc]) => ({ id, name: doc.name || "Untitled" }));
        setDocs(entries);
      })
      .catch(() => {
        if (!cancelled) setDocs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, debouncedQuery]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
    else setQuery("");
  }, [open]);

  const select = (doc: DocOption | null) => {
    onChange(doc);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-sm">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:border-[#1d365d]/40 focus:outline-none focus:ring-2 focus:ring-[#1d365d]/20 focus:border-[#1d365d]/40 transition-colors"
      >
        <span className={value ? "text-gray-900 truncate" : "text-gray-400"}>
          {value ? value.name : "All documents"}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {value && (
            <span
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                select(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={13} />
            </span>
          )}
          <ChevronDown
            size={14}
            className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {/* Search */}
          <div className="px-2 pt-2 pb-1 border-b border-gray-100">
            <div className="relative">
              <Search
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search documents…"
                className="w-full pl-7 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#1d365d]/20 focus:border-[#1d365d]/40"
              />
            </div>
          </div>

          {/* Options */}
          <ul className="max-h-56 overflow-y-auto py-1">
            <li>
              <button
                type="button"
                onClick={() => select(null)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${!value ? "text-[#1d365d] font-medium" : "text-gray-700"}`}
              >
                All documents
              </button>
            </li>
            {loading ? (
              <li className="px-3 py-2 text-sm text-gray-400">Loading…</li>
            ) : docs.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400">
                No documents found
              </li>
            ) : (
              docs.map((doc) => (
                <li key={doc.id}>
                  <button
                    type="button"
                    onClick={() => select(doc)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors truncate ${value?.id === doc.id ? "text-[#1d365d] font-medium bg-blue-50/50" : "text-gray-700"}`}
                  >
                    {doc.name}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DocumentSelect;
